import React, { useCallback, useEffect, useRef, useState } from "react";
import { login } from "../../services/authService";
import { Eye, EyeOff } from "lucide-react";

export const LoginPage: React.FC = () => {
  const emailInputRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      window.location.replace("/#/");
      return;
    }

    const expired = sessionStorage.getItem("sessionExpired");
    if (expired) {
      setError("Your session has expired. Please log in again.");
      sessionStorage.removeItem("sessionExpired");
    }
  }, []);

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e?.preventDefault();
      if (loading) return;

      if (email.length < 6 || email.length > 30) {
        setError("Username must be between 6 and 30 characters.");
        return;
      }
      if (password.length < 3 || password.length > 50) {
        setError("Password must be between 3 and 50 characters.");
        return;
      }

      setError("");
      setLoading(true);
      try {
        await login(email.trim(), password.trim());
        window.location.replace("/#/");
      } catch (err: any) {
        if (err.response?.status === 400) {
          setError("Invalid username or password.");
        } else {
          setError(err.message || "Unexpected error occurred.");
        }
      } finally {
        setLoading(false);
      }
    },
    [email, password, loading]
  );

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#0d1117] via-[#1a1f2b] to-[#0d1117] flex items-center justify-center px-4">
      <div
        className="w-full max-w-md bg-[#161B22] text-white rounded-2xl shadow-2xl pl-8 pr-8 pb-8
                      border border-[#30363d] animate-fade-in-up"
      >
        <div className="text-3xl font-bold text-center flex justify-center items-center gap-2">
          <img
            src="/images/logo/logo_login.png"
            alt="IQCRYPTO"
            className="w-[270px] h-auto sm:w-[300px] md:w-[350px] object-contain"
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-semibold mb-1 text-gray-400"
            >
              Username
            </label>
            <input
              id="email"
              ref={emailInputRef}
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value.trimStart())}
              required
              autoComplete="username"
              className="w-full px-4 py-2 rounded-lg border border-gray-600 bg-[#0d1117] text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-semibold mb-1 text-gray-400"
            >
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value.trimStart())}
                required
                autoComplete="current-password"
                className="w-full px-4 py-2 pr-10 rounded-lg border border-gray-600 bg-[#0d1117] text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                tabIndex={-1}
                aria-label="Toggle password visibility"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {error && <p className="text-red-500 text-sm text-center">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-purple-600 hover:bg-purple-700 transition-all duration-200 font-semibold py-2 px-4 rounded-lg text-white shadow-md disabled:opacity-50"
          >
            {loading ? (
              <span className="animate-pulse">Logging in...</span>
            ) : (
              "Log In"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};
